import React, { useContext, useState } from "react"
import { Div, Text } from "atomize"
import { AnimatePresence, motion } from "framer-motion"
import { modalContext } from "../providers/modal.provider"
import { theme } from "../settings/config"
import { HELP_TIPS, guideTopic } from "./guide"
import GuideText, { GuideLines } from "./GuideText"

// ──────────────────────────────────────────────────────────────────────────
// IL "?"
//
// Accanto alle cose che confondono — le presenze degli abbonati, un corso
// che salta, i compensi — un "?" dice in tre righe come funzionano, e porta
// all'argomento della guida che le spiega per intero (HELP_TIPS in
// src/help/guide.js).
//
// Su una pagina le tre righe si aprono nel foglio dal basso. Dentro un
// foglio no: l'app ne tiene aperto uno solo, e aprirne un secondo chiuderebbe
// quello in cui si sta lavorando, con quello che ci si era scritto. Lì si
// aprono sotto, al loro posto (`inline`), e per lo stesso motivo anche il
// rimando alla guida apre l'argomento lì, invece che nel foglio della guida.
// ──────────────────────────────────────────────────────────────────────────

export function HelpButton({ tip, color = theme.colors.gunmetal_fill, isOpen, onClick }) {
	return (
		<motion.button
			type="button"
			aria-label={HELP_TIPS[tip]?.title}
			aria-expanded={isOpen}
			whileTap={{ scale: 0.9 }}
			onClick={onClick}
			style={{
				flexShrink: 0,
				width: "1.5rem",
				height: "1.5rem",
				border: 0,
				padding: 0,
				borderRadius: "50%",
				background: color,
				color: theme.colors.white,
				fontFamily: "inherit",
				fontSize: "0.85rem",
				fontWeight: 700,
				lineHeight: 1,
				cursor: "pointer",
			}}>
			?
		</motion.button>
	)
}

// Il rimando all'argomento della guida: apre il foglio della guida, oppure
// fa quello che gli si chiede (`onClick`) dove un foglio nuovo non si può
export function GuideLink({ topic, onClick }) {
	const Modal = useContext(modalContext)
	const target = guideTopic(topic)
	if (!target) return null

	return (
		<Text
			tag="button"
			bg="transparent"
			p="0"
			textSize="body"
			textColor={theme.colors.primary}
			textWeight="500"
			cursor="pointer"
			style={{ border: 0, fontFamily: "inherit", textAlign: "left" }}
			onClick={() => (onClick ? onClick() : Modal.open("guide", { topicID: topic }))}>
			{`${target.title}, nella guida`}
		</Text>
	)
}

// Le tre righe al loro posto, dentro un foglio: un blocco bianco sul fondo
// caldo, senza titolo, che il "?" sta già accanto a quello di cui parla. Il
// rimando apre qui sotto l'argomento intero.
function InlinePanel({ tip }) {
	const content = HELP_TIPS[tip]
	const topic = guideTopic(content.topic)
	const [showsTopic, setShowsTopic] = useState(false)

	return (
		<motion.div
			initial={{ height: 0, opacity: 0 }}
			animate={{ height: "auto", opacity: 1 }}
			exit={{ height: 0, opacity: 0 }}
			transition={{ duration: 0.2 }}
			style={{ overflow: "hidden" }}>
			<Div
				bg={theme.colors.surface}
				rounded="15px"
				p="0.75rem 1rem 0.85rem"
				m={{ y: "0.5rem" }}>
				<GuideLines lines={content.lines} />
				{showsTopic && topic ? (
					<Div m={{ t: "0.75rem" }}>
						<Text tag="h3" textWeight="600" textSize="subheader" m={{ b: "0.5rem" }}>
							{topic.title}
						</Text>
						<GuideText blocks={topic.blocks} blockBg={theme.colors.background} />
					</Div>
				) : (
					<GuideLink topic={content.topic} onClick={() => setShowsTopic(true)} />
				)}
			</Div>
		</motion.div>
	)
}

// Quello di cui si parla, col suo "?" accanto. `inline` dentro un foglio.
function HelpTip({ tip, inline = false, color, justify = "flex-start", m, children }) {
	const Modal = useContext(modalContext)
	const [isOpen, setOpen] = useState(false)
	if (!HELP_TIPS[tip]) return children ?? null

	return (
		<Div m={m}>
			<Div d="flex" align="center" justify={justify} style={{ gap: "0.5rem" }}>
				{children}
				<HelpButton
					tip={tip}
					color={color}
					isOpen={inline ? isOpen : undefined}
					onClick={() => (inline ? setOpen(!isOpen) : Modal.open("helpTip", tip))}
				/>
			</Div>
			{inline && (
				<AnimatePresence initial={false}>
					{isOpen && <InlinePanel tip={tip} />}
				</AnimatePresence>
			)}
		</Div>
	)
}

export default HelpTip
